import * as XLSX from "xlsx";

/**
 * ADR-0154 §6.3 C5 — these vocabularies have one console home
 * (`semanticModelVocab.ts`), parity-gated against the hub validator. They were
 * declared here with an "Aligned with hub …" comment, which is a claim rather
 * than a mechanism. Re-exported so this module's importers are unchanged.
 */
export {
  SEMANTIC_FIELD_DATA_TYPES,
  SEMANTIC_SECURITY_CLASSES,
  SEMANTIC_EXPOSURE_MODES,
} from "./semanticModelVocab.js";

import {
  LEGACY_SECURITY_CLASSES,
  SEMANTIC_EXPOSURE_MODES,
  SEMANTIC_FIELD_DATA_TYPES,
  SEMANTIC_SECURITY_CLASSES,
  legacyAccessValue,
} from "./semanticModelVocab.js";


const FIELD_TYPE_SET = new Set<string>(SEMANTIC_FIELD_DATA_TYPES);

const SECURITY_SET = new Set<string>(SEMANTIC_SECURITY_CLASSES);
const LEGACY_SECURITY_SET = new Set<string>(LEGACY_SECURITY_CLASSES);

const EXPOSURE_SET = new Set<string>(SEMANTIC_EXPOSURE_MODES);

export type SemanticImportSeverity = "ok" | "warning" | "error";

export type SemanticImportConflictMode = "replace" | "merge" | "skip";

/** Canonical column key -> list of accepted header labels (lowercase). */
const HEADER_TO_CANON: Record<string, string[]> = {
  object_name: ["object_name", "object", "table_name", "table", "entity"],
  column_name: ["column_name", "column", "field_name", "field"],
  data_type: ["data_type", "type", "datatype"],
  pk: ["pk", "primary_key", "key", "primary"],
  security_class: ["security_class", "security", "classification"],
  exposure_mode: ["exposure_mode", "exposure"],
  table_description: [
    "table_description",
    "object_description",
    "table_desc",
    "object_desc",
    "table description",
    "object description"
  ],
  column_description: [
    "column_description",
    "field_description",
    "column_desc",
    "field_desc",
    "column description",
    "field description"
  ]
};

const CANON_KEYS = Object.keys(HEADER_TO_CANON) as (keyof typeof HEADER_TO_CANON)[];

function normalizeHeaderCell(v: unknown): string {
  return String(v ?? "")
    .trim()
    .toLowerCase();
}

/** Canonical field name -> sheet column index */
function buildHeaderMap(headerRow: unknown[]): Map<string, number> | null {
  const byCanon = new Map<string, number>();
  for (let c = 0; c < headerRow.length; c++) {
    const h = normalizeHeaderCell(headerRow[c]);
    if (!h) continue;
    for (const canon of CANON_KEYS) {
      if (HEADER_TO_CANON[canon].includes(h)) {
        if (!byCanon.has(canon)) byCanon.set(canon, c);
        break;
      }
    }
  }
  const need: (keyof typeof HEADER_TO_CANON)[] = ["object_name", "column_name", "data_type"];
  for (const k of need) {
    if (byCanon.get(k) === undefined) return null;
  }
  return byCanon;
}

function cell(row: unknown[], colIndex: number | undefined): string {
  if (colIndex === undefined) return "";
  const v = row[colIndex];
  if (v == null) return "";
  if (typeof v === "string") return v.trim();
  if (typeof v === "number" || typeof v === "boolean") return String(v).trim();
  return String(v).trim();
}

function mapDataType(raw: string): { value: string; warnings: string[] } {
  const w: string[] = [];
  // Strip length/precision suffixes like (10), (18,2) before matching
  const t = raw.trim().toLowerCase().replace(/\s*\(.*\)$/, "");
  if (!t) return { value: "varchar", warnings: [] };
  if (FIELD_TYPE_SET.has(t)) return { value: t, warnings: w };

  let mapped: string | null = null;
  if (/^(string|str|char|nchar|nvarchar|uniqueidentifier|uuid|guid|sysname)$/.test(t)) {
    mapped = "varchar";
  } else if (/^(integer|int32|long|smallint|tinyint|bigint|serial)$/.test(t)) {
    mapped = "int";
  } else if (/^(number|float|double|numeric|money|smallmoney|real)$/.test(t)) {
    mapped = "decimal";
  } else if (/^(bool|bit)$/.test(t)) {
    mapped = "boolean";
  } else if (/^(smalldatetime|datetime2|timestamp)$/.test(t)) {
    mapped = "datetime";
  } else if (/^(time)$/.test(t)) {
    mapped = "datetime";
  } else if (/^(ntext|xml|clob|longvarchar|json)$/.test(t)) {
    mapped = "text";
  } else if (/^(image|varbinary|binary|bytea)$/.test(t)) {
    mapped = "blob";
  }

  if (mapped) {
    if (mapped !== t) w.push(`Data type "${raw}" was mapped to ${mapped}.`);
    return { value: mapped, warnings: w };
  }
  w.push(`Unrecognized data type "${raw}"; defaulted to varchar.`);
  return { value: "varchar", warnings: w };
}

/**
 * ADR-0212 §4.2 — a column's access is allow or restrict. The four classes it
 * replaced are still read from older workbooks, mapped by the table hub
 * migration 282 applied, with a warning on the row saying what each was read as.
 */
function mapSecurityClass(raw: string): { value: string; warnings: string[] } {
  const t = raw.trim().toLowerCase();
  if (!t) return { value: "allow", warnings: [] };
  if (SECURITY_SET.has(t)) return { value: t, warnings: [] };
  if (LEGACY_SECURITY_SET.has(t)) {
    const value = legacyAccessValue(t);
    return { value, warnings: [`"${raw.trim()}" read as ${value}.`] };
  }
  return {
    value: "allow",
    warnings: [`Unrecognized security_class "${raw}"; defaulted to allow.`]
  };
}

function mapExposureMode(raw: string): { value: string; warnings: string[] } {
  const t = raw.trim().toLowerCase();
  if (!t) return { value: "full", warnings: [] };
  if (EXPOSURE_SET.has(t)) return { value: t, warnings: [] };
  return {
    value: "full",
    warnings: [`Unrecognized exposure_mode "${raw}"; defaulted to full.`]
  };
}

export type ParsedImportRow = {
  id: string;
  sheetRowNumber: number;
  objectName: string;
  columnName: string;
  dataType: string;
  primaryKey: boolean;
  securityClass: string;
  exposureMode: string;
  /** Optional; first non-blank in sheet order for this object becomes the semantic object description on import. */
  tableDescription: string;
  /** Optional; becomes the field `description`. */
  columnDescription: string;
  parseSeverity: SemanticImportSeverity;
  parseMessages: string[];
};

export type ParseWorkbookResult =
  | { ok: true; rows: ParsedImportRow[] }
  | { ok: false; error: string };

export function parseSemanticImportWorkbook(buffer: ArrayBuffer): ParseWorkbookResult {
  let wb: XLSX.WorkBook;
  try {
    wb = XLSX.read(buffer, { type: "array" });
  } catch {
    return { ok: false, error: "Could not read this file as an Excel workbook." };
  }
  const sheetName = wb.SheetNames[0];
  if (!sheetName) return { ok: false, error: "Workbook has no sheets." };
  const sheet = wb.Sheets[sheetName];
  const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" }) as unknown[][];
  if (matrix.length < 2) return { ok: false, error: "Sheet needs a header row and at least one data row." };

  const headerMap = buildHeaderMap(matrix[0] ?? []);
  if (!headerMap) {
    return {
      ok: false,
      error:
        "Missing required columns. Include headers for object name, column/field name, and data type (aliases accepted per import help)."
    };
  }

  const idx = {
    object_name: headerMap.get("object_name")!,
    column_name: headerMap.get("column_name")!,
    data_type: headerMap.get("data_type")!,
    pk: headerMap.get("pk"),
    security_class: headerMap.get("security_class"),
    exposure_mode: headerMap.get("exposure_mode"),
    table_description: headerMap.get("table_description"),
    column_description: headerMap.get("column_description")
  };
  const hasSecurityCol = idx.security_class !== undefined;
  const hasExposureCol = idx.exposure_mode !== undefined;

  const rows: ParsedImportRow[] = [];
  for (let r = 1; r < matrix.length; r++) {
    const row = matrix[r] ?? [];
    const sheetRowNumber = r + 1;
    const objectName = cell(row, idx.object_name);
    const columnName = cell(row, idx.column_name);
    const dataTypeRaw = cell(row, idx.data_type);
    const pkRaw = cell(row, idx.pk);
    const secRaw = cell(row, idx.security_class);
    const expRaw = cell(row, idx.exposure_mode);
    const tableDescRaw = cell(row, idx.table_description);
    const columnDescRaw = cell(row, idx.column_description);

    if (!objectName && !columnName && !dataTypeRaw) continue;

    const parseMessages: string[] = [];
    let parseSeverity: SemanticImportSeverity = "ok";

    if (!objectName) {
      parseMessages.push("Missing object_name.");
      parseSeverity = "error";
    }
    if (!columnName) {
      parseMessages.push("Missing column_name.");
      parseSeverity = "error";
    }
    if (!dataTypeRaw) {
      parseMessages.push("Missing data_type.");
      parseSeverity = "error";
    }

    const dt = dataTypeRaw ? mapDataType(dataTypeRaw) : { value: "varchar", warnings: [] as string[] };
    const sc = mapSecurityClass(secRaw);
    const ex = mapExposureMode(expRaw);

    for (const m of dt.warnings) {
      parseMessages.push(m);
      parseSeverity = parseSeverity === "error" ? "error" : "warning";
    }
    for (const m of sc.warnings) {
      parseMessages.push(m);
      parseSeverity = parseSeverity === "error" ? "error" : "warning";
    }
    for (const m of ex.warnings) {
      parseMessages.push(m);
      parseSeverity = parseSeverity === "error" ? "error" : "warning";
    }

    if (hasSecurityCol && !secRaw.trim()) {
      parseMessages.push("security_class empty; using default allow.");
      if (parseSeverity === "ok") parseSeverity = "warning";
    }
    if (hasExposureCol && !expRaw.trim()) {
      parseMessages.push("exposure_mode empty; using default full.");
      if (parseSeverity === "ok") parseSeverity = "warning";
    }

    const primaryKey = pkRaw.length > 0;

    rows.push({
      id: `row-${sheetRowNumber}`,
      sheetRowNumber,
      objectName,
      columnName,
      dataType: dt.value,
      primaryKey,
      securityClass: sc.value,
      exposureMode: ex.value,
      tableDescription: tableDescRaw,
      columnDescription: columnDescRaw,
      parseSeverity,
      parseMessages
    });
  }

  return { ok: true, rows };
}

function severityRank(s: SemanticImportSeverity): number {
  switch (s) {
    case "error":
      return 3;
    case "warning":
      return 2;
    default:
      return 1;
  }
}

export function maxSeverity(a: SemanticImportSeverity, b: SemanticImportSeverity): SemanticImportSeverity {
  return severityRank(a) >= severityRank(b) ? a : b;
}

export type SemanticFieldShape = {
  id: string;
  name: string;
  data_type: string;
  primary_key?: boolean;
  description?: string | null;
  output_format?: string | null;
  notes?: string | null;
  security_profile: { security_class: string; exposure_mode: string };
};

export type SemanticObjectShape = {
  id: string;
  name: string;
  description?: string | null;
  source: Record<string, unknown>;
  fields: SemanticFieldShape[];
};

export type SemanticSpecImportShape = {
  version: "semantic.v1";
  objects: SemanticObjectShape[];
  relationships: unknown[];
  subject_areas: Array<{
    id: string;
    name: string;
    description?: string | null;
    is_master?: boolean;
    display_mode?: string;
    object_placements: Array<{ object_id: string; ui: { x: number; y: number }; restricted_field_ids?: string[] }>;
    notes?: Array<{ id: string; text: string; ui: { x: number; y: number } }>;
    ui?: { viewport?: { x: number; y: number; zoom: number } } | null;
  }>;
};

function findExistingObject(spec: SemanticSpecImportShape, objectName: string): SemanticObjectShape | undefined {
  const t = objectName.trim().toLowerCase();
  return spec.objects.find((o) => o.name.trim().toLowerCase() === t);
}

/** Group key = trimmed object name from file (exact match, case-sensitive trim). */
export function groupRowsByObjectName(rows: ParsedImportRow[]): Map<string, ParsedImportRow[]> {
  const m = new Map<string, ParsedImportRow[]>();
  for (const row of rows) {
    const key = row.objectName.trim();
    const list = m.get(key) ?? [];
    list.push(row);
    m.set(key, list);
  }
  return m;
}

export type ImportRowReview = {
  row: ParsedImportRow;
  severity: SemanticImportSeverity;
  messages: string[];
};

export type ImportObjectGroup = {
  objectKey: string;
  displayName: string;
  existingObjectId: string | undefined;
  hasConflict: boolean;
  rows: ImportRowReview[];
  groupSeverity: SemanticImportSeverity;
  groupMessages: string[];
};

export function buildImportReview(
  spec: SemanticSpecImportShape,
  parsedRows: ParsedImportRow[],
  resolutions: Record<string, SemanticImportConflictMode>
): ImportObjectGroup[] {
  const grouped = groupRowsByObjectName(parsedRows);
  const keys = [...grouped.keys()].sort((a, b) => a.localeCompare(b));
  const out: ImportObjectGroup[] = [];

  for (const objectKey of keys) {
    const groupRows = grouped.get(objectKey)!;
    const displayName = objectKey;
    const existing = findExistingObject(spec, objectKey);
    const hasConflict = !!existing;
    const resolution = hasConflict ? (resolutions[objectKey] ?? "merge") : "merge";

    const rowReviews: ImportRowReview[] = [];
    const groupMessages: string[] = [];

    if (hasConflict) {
      groupMessages.push("Matches an existing model object; choose Replace, Merge, or Skip.");
      if (resolution === "replace") {
        groupMessages.push("All existing fields on this object will be replaced.");
      } else if (resolution === "skip") {
        groupMessages.push("This object will not be imported.");
      }
    }

    const existingFieldNamesLower = new Set(
      (existing?.fields ?? []).map((f) => f.name.trim().toLowerCase())
    );

    for (const row of groupRows) {
      const messages = [...row.parseMessages];
      let severity = row.parseSeverity;

      if (severity !== "error" && hasConflict) {
        if (resolution === "skip") {
          messages.push("Object will not be imported (Skip).");
          severity = maxSeverity(severity, "warning");
        } else if (resolution === "merge") {
          const cn = row.columnName.trim().toLowerCase();
          if (cn && existingFieldNamesLower.has(cn)) {
            messages.push("Field already exists on the model; will be skipped on Merge.");
            severity = maxSeverity(severity, "warning");
          }
        }
      }

      rowReviews.push({ row, severity, messages });
    }

    let groupSeverity: SemanticImportSeverity = "ok";
    for (const gm of groupMessages) {
      if (gm.includes("Skip")) groupSeverity = maxSeverity(groupSeverity, "warning");
    }
    for (const rr of rowReviews) {
      groupSeverity = maxSeverity(groupSeverity, rr.severity);
    }

    out.push({
      objectKey,
      displayName,
      existingObjectId: existing?.id,
      hasConflict,
      rows: rowReviews,
      groupSeverity,
      groupMessages
    });
  }

  return out;
}

export type ApplySemanticImportOptions = {
  includedRowIds: Set<string>;
  /** false = exclude whole object regardless of row checkboxes */
  objectIncluded: Record<string, boolean>;
  resolutions: Record<string, SemanticImportConflictMode>;
  activeSaId: string;
  /** For tests */
  randomId?: () => string;
};

function effectiveRowIncluded(
  objectKey: string,
  rowId: string,
  objectIncluded: Record<string, boolean>,
  includedRowIds: Set<string>
): boolean {
  const oi = objectIncluded[objectKey];
  if (oi === false) return false;
  return includedRowIds.has(rowId);
}

export function hasBlockingIncludedError(
  groups: ImportObjectGroup[],
  objectIncluded: Record<string, boolean>,
  includedRowIds: Set<string>
): boolean {
  for (const g of groups) {
    for (const rr of g.rows) {
      if (rr.severity !== "error") continue;
      if (effectiveRowIncluded(g.objectKey, rr.row.id, objectIncluded, includedRowIds)) return true;
    }
  }
  return false;
}

/** True when Apply should be enabled: no included error rows and at least one field row will be applied. */
export function canApplySemanticImport(
  groups: ImportObjectGroup[],
  objectIncluded: Record<string, boolean>,
  includedRowIds: Set<string>,
  resolutions: Record<string, SemanticImportConflictMode>
): boolean {
  if (hasBlockingIncludedError(groups, objectIncluded, includedRowIds)) return false;
  return summarizeImportApply(groups, objectIncluded, includedRowIds, resolutions).fieldRowsApplied > 0;
}

export function summarizeImportApply(
  groups: ImportObjectGroup[],
  objectIncluded: Record<string, boolean>,
  includedRowIds: Set<string>,
  resolutions: Record<string, SemanticImportConflictMode>
): { newObjects: number; merged: number; replaced: number; skipped: number; fieldRowsApplied: number } {
  let newObjects = 0;
  let merged = 0;
  let replaced = 0;
  let skipped = 0;
  let fieldRowsApplied = 0;

  for (const g of groups) {
    const rowsIn = g.rows.filter((rr) => effectiveRowIncluded(g.objectKey, rr.row.id, objectIncluded, includedRowIds));
    const validIn = rowsIn.filter((r) => r.row.parseSeverity !== "error");
    if (validIn.length === 0) continue;

    if (g.hasConflict) {
      const mode = resolutions[g.objectKey] ?? "merge";
      if (mode === "skip") {
        skipped++;
        continue;
      }
      if (mode === "replace") replaced++;
      else merged++;
      fieldRowsApplied += validIn.length;
    } else {
      newObjects++;
      fieldRowsApplied += validIn.length;
    }
  }

  return { newObjects, merged, replaced, skipped, fieldRowsApplied };
}

/** First non-blank `tableDescription` in row order (sheet order within the object). */
export function firstTableDescriptionForRows(rows: ParsedImportRow[]): string | null {
  for (const row of rows) {
    const t = row.tableDescription.trim();
    if (t) return t;
  }
  return null;
}

function newFieldFromRow(row: ParsedImportRow, randomId: () => string): SemanticFieldShape {
  const cd = row.columnDescription.trim();
  return {
    id: randomId(),
    name: row.columnName.trim(),
    data_type: row.dataType,
    primary_key: row.primaryKey,
    description: cd ? cd : null,
    output_format: null,
    notes: null,
    security_profile: { security_class: row.securityClass, exposure_mode: row.exposureMode }
  };
}

export function applySemanticImportToSpec(
  spec: SemanticSpecImportShape,
  parsedRows: ParsedImportRow[],
  options: ApplySemanticImportOptions
): SemanticSpecImportShape {
  const { includedRowIds, objectIncluded, resolutions, activeSaId, randomId } = options;
  const rid = randomId ?? (() => crypto.randomUUID());

  const next = JSON.parse(JSON.stringify(spec)) as SemanticSpecImportShape;
  const master = next.subject_areas.find((s) => s.is_master);
  const cur = next.subject_areas.find((s) => s.id === activeSaId);
  if (!cur) return next;

  const grouped = groupRowsByObjectName(parsedRows);
  const keys = [...grouped.keys()].sort((a, b) => a.localeCompare(b));

  let newObjIndex = 0;
  for (const objectKey of keys) {
    const allGroupRows = grouped.get(objectKey)!;
    const rows = allGroupRows.filter((r) => effectiveRowIncluded(objectKey, r.id, objectIncluded, includedRowIds));
    const validRows = rows.filter((r) => r.parseSeverity !== "error");
    if (validRows.length === 0) continue;

    const objectDesc = firstTableDescriptionForRows(validRows);

    const existing = findExistingObject(next, objectKey);
    const resolution = existing ? (resolutions[objectKey] ?? "merge") : "merge";

    if (existing) {
      if (resolution === "skip") continue;

      const obj = next.objects.find((o) => o.id === existing.id);
      if (!obj) continue;

      if (resolution === "replace") {
        // ADR-0212 (E4, ruling 25): a row naming a column the object already has keeps that column's id —
        // case-insensitive, as infer-schema's merge does — so the joins mapped on it and any subject area's
        // restriction of it survive the replace. Only a new column gets a new id.
        const existingIds = new Map(obj.fields.map((f) => [f.name.trim().toLowerCase(), f.id] as const));
        obj.fields = validRows.map((row) => {
          const field = newFieldFromRow(row, rid);
          const key = field.name.toLowerCase();
          const kept = existingIds.get(key);
          if (!kept) return field;
          existingIds.delete(key); // a second row naming the same column must not share its id
          return { ...field, id: kept };
        });
        obj.description = objectDesc;
      } else {
        const byName = new Map(obj.fields.map((f) => [f.name.trim().toLowerCase(), f] as const));
        for (const row of validRows) {
          const k = row.columnName.trim().toLowerCase();
          if (!k) continue;
          if (byName.has(k)) continue;
          const f = newFieldFromRow(row, rid);
          obj.fields.push(f);
          byName.set(k, f);
        }
        if (objectDesc !== null) obj.description = objectDesc;
      }
      continue;
    }

    const oid = rid();
    const objectName = objectKey.trim() || `Object ${next.objects.length + 1}`;
    next.objects.push({
      id: oid,
      name: objectName,
      description: objectDesc,
      source: { type: "view", name: "" },
      fields: validRows.map((row) => newFieldFromRow(row, rid))
    });

    const px = 120 + newObjIndex * 48;
    const py = 120 + newObjIndex * 48;
    newObjIndex++;

    cur.object_placements.push({ object_id: oid, ui: { x: px, y: py } });
    if (master && master.id !== cur.id) {
      master.object_placements.push({ object_id: oid, ui: { x: 0, y: 0 } });
    }
  }

  return next;
}

/** Suggested download filename for the sample workbook. */
export const SAMPLE_SEMANTIC_IMPORT_FILENAME = "semantic-model-import-sample.xlsx";

const SAMPLE_SHEET_IMPORT = "Import";
const SAMPLE_SHEET_COLUMNS = "Columns";

/**
 * Builds a small .xlsx with (1) example rows using every import column and every field data type,
 * and (2) a reference sheet describing each column and accepted header aliases.
 */
export function buildSampleSemanticImportWorkbookBuffer(): ArrayBuffer {
  const importMatrix: unknown[][] = [
    [
      "object_name",
      "column_name",
      "data_type",
      "pk",
      "security_class",
      "exposure_mode",
      "table_description",
      "column_description"
    ],
    [
      "Purchase Order",
      "po_number",
      "varchar",
      "X",
      "allow",
      "full",
      "Purchase order document (header).",
      "Unique purchase order identifier."
    ],
    ["Purchase Order", "line_number", "int", "X", "allow", "full", "", "Line number within the PO."],
    ["Purchase Order", "unit_price", "decimal", "", "allow", "masked", "", "Unit price for the line."],
    ["Purchase Order", "buyer_name", "varchar", "", "restrict", "masked", "", "Buyer display name."],
    ["Part", "part_number", "varchar", "X", "", "", "", "Stock keeping identifier."],
    ["Part", "description", "text", "", "", "", "Inventory part master record.", "Long-form part description."],
    ["Part", "standard_cost", "decimal", "", "restrict", "aggregate_only", "", "Internal cost (aggregated exposure)."],
    ["Customer", "customer_id", "varchar", "X", "", "", "Customer account.", "External customer identifier."],
    ["Customer", "credit_limit", "decimal", "", "restrict", "restricted", "", "Approved credit ceiling."],
    ["Product", "is_active", "boolean", "", "allow", "full", "Sellable product.", "Whether the product is offered for sale."],
    ["Product", "launched_on", "date", "", "allow", "full", "", "Market launch date."],
    ["Product", "updated_at", "datetime", "", "allow", "full", "", "Last update timestamp."],
    ["Product", "image_bytes", "blob", "", "allow", "full", "", "Binary image payload."]
  ];

  const columnsMatrix: unknown[][] = [
    ["Column", "Required", "Description"],
    [
      "object_name",
      "Yes",
      "Semantic object name. Rows with the same value become fields on one object."
    ],
    ["column_name", "Yes", "Field name."],
    [
      "data_type",
      "Yes",
      `Field type: ${SEMANTIC_FIELD_DATA_TYPES.join(", ")}. Aliases such as string, integer, number, bool are accepted; unknown values default to varchar with a warning.`
    ],
    ["pk", "No", "Any non-empty value marks the field as a primary key."],
    [
      "security_class",
      "No",
      `Column access. Default allow. One of: ${SEMANTIC_SECURITY_CLASSES.join(", ")}. The older words low, confidential, restricted and prohibited are read as allow, allow, restrict and restrict.`
    ],
    [
      "exposure_mode",
      "No",
      `Default full. One of: ${SEMANTIC_EXPOSURE_MODES.join(", ")}.`
    ],
    [
      "table_description",
      "No",
      "Semantic object description. The first non-blank value in sheet order for that object_name is applied to the object (new or merge/replace)."
    ],
    [
      "column_description",
      "No",
      "Field description; applied to each imported column."
    ],
    [
      "",
      "",
      "Optional columns may be omitted from the sheet entirely; if present, blank cells use defaults (see warnings in the import preview)."
    ],
    [
      "Header aliases",
      "",
      "Headers are matched case-insensitive. Alternatives: object, table, entity → object_name; column, field → column_name; type, datatype → data_type; primary_key, key, primary → pk; security, classification → security_class; exposure → exposure_mode; object_description, table_desc → table_description; field_description, column_desc → column_description."
    ]
  ];

  const wsImport = XLSX.utils.aoa_to_sheet(importMatrix);
  const wsColumns = XLSX.utils.aoa_to_sheet(columnsMatrix);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, wsImport, SAMPLE_SHEET_IMPORT);
  XLSX.utils.book_append_sheet(wb, wsColumns, SAMPLE_SHEET_COLUMNS);
  const raw = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const u8 = raw instanceof Uint8Array ? raw : new Uint8Array(raw as ArrayLike<number>);
  const out = new ArrayBuffer(u8.byteLength);
  new Uint8Array(out).set(u8);
  return out;
}
